/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isNull from 'lodash.isnull';
import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import {v4} from 'uuid';
import {error} from '../logger/logger';
import {GenericObject} from '../types/entities';

export type NormalizeClauseOptions = {
  field: string;
  operator: 'OR' | 'AND';
  caseSensitive?: boolean;
};

const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// defines the parameters definitions for padStart method...
type PadParam = string | number;

/**
 * The isEscapeCharacter function provides a simply utility which checks for special charactors by.
 * by checking ascii value. if characters % or _ or [ or ' or & found, returns true, false otherwise.
 *
 * ```typescript
 *
 *   import {isEscapeCharacter} from '@ion/core/src/utils/';
 *   const strWithEscaping = isEscapeCharacter('['); //output: true
 *
 * ```
 */
export const isEscapeCharacter = (character: string): boolean => {
  const ascii = character.charCodeAt(0);
  return ((
    ascii === 39 ||
    ascii === 38 ||
    ascii === 37 ||
    ascii === 95 ||
    ascii === 91
  ));
};

/**
 * The isSpecialCharacter function provides a simply utility which checks for special charactors by.
 * by checking ascii value.
 *
 * ```typescript
 *
 *   import {isSpecialCharacter} from '@ion/core/src/utils/';
 *
 *   const isSplChr = isSpecialCharacter('['); //output: true
 *
 * ```
 */
export const isSpecialCharacter = (character: string): boolean => {
  const ascii = character.charCodeAt(0);
  return ((
    (ascii >= 33 && ascii <= 47) ||
    (ascii >= 58 && ascii <= 64) ||
    (ascii >= 91 && ascii <= 96) ||
    (ascii >= 123 && ascii <= 126)
  ));
};

/**
 * The stringWithEscapeCharacters function provides a simply utility which adds escape charactors to special charactors
 * in the string. Special charactors needs escaping are %, _, [, ', &.
 *
 * ```typescript
 *
 *   import {stringWithEscapeCharacters} from '@ion/core/src/utils/';
 *   const strWithEscaping = stringWithEscapeCharacters('C%][^t_n'); //output: C[%]][[]^t[_]n
 *
 * ```
 */
export const stringWithEscapeCharacters = (input: string): string => {
  let output = '';
  if (input) {
    input.split('').forEach((character: string) => {
      if (isEscapeCharacter(character)) {
        if (character === '\'') {
          output = output + '\'\'';
        } else {
          output = output + '[' + character + ']';
        }
      } else {
        output = output + character;
      }
    });
  }
  return output || input;
};

/**
 * A function that escapes special characters in a string for use in a PostGreSQL query.
 * @param str (string): The string to escape.
 * @returns (string): The escaped version of the input string.
 *
 * ```typescript
 * import { escapePostgreSQLString } from './utils';
 *
 * const unsafeString = "Hello 'world' \\";
 * const safeString = escapePostgreSQLString(unsafeString);
 *
 * console.log(unsafeString); // "Hello 'world' \"
 * console.log(safeString);   // "Hello \\'world\\' \\\\"
 */

export const escapePostgreSQLString = (str: string): string => {
  return str.replace(/\\/g, '\\\\')
    .replace(/'/g, '\\\'')
    .replace(/"/g, '\\"')
    .replace(/\0/g, '\\0');
};

/**
 * The trimAll function provides a simply utility which removes all
 * white spaces.
 *
 * ```typescript
 *
 *   import {trimAll} from '@ion/core/src/utils/';
 *
 *   trimAll(' Last Week ') // 'lastweek'
 *
 * ```
 */
export const trimAll = (val: string) => {
  return val?.replace(/ /g, '');
};

/**
 * The isWhiteSpace utility returns true if the provided value
 * is an empty string or contains only whitespace characters.
 *
 * @method isWhiteSpace
 */
export const isWhiteSpace = (val: string) => /^\s*$/g.test(val);

/**
 * Given a string, returns true if the string is considered empty.
 */
export const isEmpty = (val: string) => {
  return val ? val.length <= 0 || isWhiteSpace(val) : true;
};

/**
 * Provides a utility which allows for normalizing a string by removing all
 * non single whitespace characters.
 *
 * This utility is primarily provided as a convenience to be used by clients
 * in order to aid in readability of multi-line string constants while ensuring
 * the additional white space is removed when used for display.
 *
 * ```typescript
 *   const MESSAGE = `
 *     An Error occurred while executing the ${operation} against the ${api} API.
 *     The ${api} must be invoked with the required arguments. Please see the
 *     API Documentation at ${url} for additional information.
 *   `;
 *
 *   // normalizes the message with all newlines and additional white space removed.
 *   const normalized = normalize(MESSAGE);
 * ```
 *
 * @param   value The `string` which is to have all newlines and extraneous
 *          white-space removed.
 * @returns The provided `string` with all newlines and non single whitespace
 *          characters removed.
 */
export const normalize = (value: string): string => (
  value.replace(/\s{2,}/g, ' ').trim()
);

/**
 * Given an HTML element ID candidate string, returns the specified string
 * if it's not empty; otherwise generates and returns a UUID string to be
 * used as the ID.
 *
 * ```typescript
 *
 * import {getValidElementId} from '@ion/core/src/utils/';
 *
 * const SomeComponent = (props) => {
 *   ...
 *   return (
 *     <div id={getValidElementId(props.elementId)}>
 *       ...
 *     </div>
 *   );
 * };
 *
 * ```
 *
 */
export const getValidElementId = (val?: string) => {
  return !isEmpty(val) ? val : v4();
};

/**
 * Provides a simple utility which compares two strings for equality, optionally
 * ignoring case.
 *
 * ```typescript
 *
 *   import {isEqual} from '@ion/core/src/utils/StringUtil';
 *
 *    expect(isEqual('one', 'one')).toEqual(true);
 *    expect(isEqual('one', 'ONE')).toEqual(false);
 *    expect(isEqual('one', 'ONE', true)).toEqual(true);
 *    expect(isEqual('one', 'Two', true)).toEqual(false);
 *    expect(isEqual('', '')).toEqual(true);
 *    expect(isEqual('', '', true)).toEqual(true);
 *
 * ```
 *
 * @param first      The first string in the comparison
 * @param second     The second string in the comparison
 * @param ignoreCase When true, case will be ignored (defaults to false)
 */
export const isEqual = (first: string, second: string, ignoreCase = false): boolean => {
  return ignoreCase ? first?.toLowerCase() === second?.toLowerCase() : first === second;
};

/**
 * Provides a simple utility which compares to strings for non-equality,
 * optionally ignoring case. This utility is essentially the inverse of
 * isEqual.
 *
 * ```typescript
 *
 *   import {notEqual} from '@ion/core/src/utils/';
 *
 *    expect(notEqual('one', 'two')).toEqual(true);
 *    expect(notEqual('one', 'One')).toEqual(true);
 *    expect(notEqual('one', 'one')).toEqual(false);
 *    expect(notEqual('one', 'ONE', true)).toEqual(false);
 *    expect(notEqual('', '')).toEqual(false);
 *    expect(notEqual('', '', true)).toEqual(false);
 *
 * ```
 *
 * @param first      The first string in the comparison
 * @param second     The second string in the comparison
 * @param ignoreCase When true, case will be ignored (defaults to false)
 */
export const notEqual = (first: string, second: string, ignoreCase = false): boolean => {
  return !isEqual(first, second, ignoreCase);
};

/**
 * Given a string with optional delimiter and padding amount, pads left the
 * delimiter by the padding amount specified
 */
export const padDelimiterLeft = (val: string, delimiter = ',', amount = 2) => {
  if (val) {
    const padding = new Array(amount).toString().replace(/,/g, ' ');
    return val.replace(delimiter, `${delimiter}${padding}`);
  }
  return val;
};

/**
 * The contains utility returns true if the provided search string
 * is matched within the given value.
 *
 * ```typescript
 *
 *   import {contains} from '@ion/core/src/utils/';
 *
 *   expect(contains('/tracking/cvs', /cvs')).to.equal(true); // true
 *
 * ```
 *
 */
export const contains = (search: string, what: string, caseSensitive = true) => {
  if (search && what) {
    let source = search.toString();
    let target = what.toString();
    if (!caseSensitive) {
      source = source.toLowerCase();
      target = target.toLowerCase();
    }
    return source.indexOf(target) > -1;
  }
};

/**
 * The toTitleCase function provides a simply utility which converts a given
 * string to Title Case.
 *
 * ```typescript
 *
 *   import {titleCase} from '@ion/core/src/utils/StringUtil';
 *
 *   titleCase('this is a title') // 'This Is A Title'
 *
 * ```
 */
export const titleCase = (val: string) => {
  return val?.split(' ')
    .map(s => s.slice(0, 1).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ') || val;
};

/**
 * Takes a string as an argument, capitalizes the
 * first letter, and returns the string
 *
 * ```typescript
 *
 *   import {capitalizeFirstLetter} from '@ion/core/src/utils/StringUtil';
 *
 *   capitalizeFirstLetter('saturday') // 'Saturday'
 *
 * ```
 */
export const capitalizeFirstLetter = (val: string | unknown) => {
  if (typeof val !== 'string') return val;
  return val.charAt(0).toUpperCase() + val.slice(1);
};

/**
 * The toStringMapper allows for converting an array of strings to an escaped
 * double quoted string.
 *
 * ```typescript
 *
 *   import {toStringMapper} from '@ion/core/src/utils/';
 *   const items = ['one', 'two', 'three'];
 *   toStringMapper(items); // "'one','two','three'"
 *
 * ```
 */
export const toStringMapper = (items: any[], formatter?: Function, lowerCase: boolean = false): string => {
  return items.map(item => {
    const value = !lowerCase ? item : item?.toLowerCase();
    return `'${formatter?.(value) ?? value}'`;
  }).toString();
};

/**
 *
 * @splitIncludedText
 *
 * Function that finds a string within a string and splits out
 * the text that is included and the remaining text from the longer (compared to)
 * string. This allows for things like rendering search results that
 * highlight text included in the search term.
 *
 * ```typescript
 *
 *   import {splitIncludedText} from '@ion/core/src/utils/';
 *   splitIncludedText('Paris, Fr', 'ari', false);
 *     // { includedText: 'ari', splitText: ['P', 'ari', 's, Fr']}
 *
 * ```
 */
export const splitIncludedText = (search: string, what: string, caseSensitive = true): {
  includedText: string;
  splitText: string[];
} => {
  if (!what) return;
  const startIndex = caseSensitive
    ? search?.indexOf(what)
    : search?.toLowerCase().indexOf(what?.toLowerCase());
  const includedText = startIndex > -1
    ? search?.substring(startIndex, startIndex + what?.length)
    : undefined;
  const splitText = search?.split(new RegExp(`(${regExpEscape(what)})`, caseSensitive ? 'g' : 'ig'));
  return {
    includedText,
    splitText
  };
};

/**
 * The splitBySpecialCharacters utility function is used to split a string into a list
 * by any special characters.
 *
 *
 * ```typescript
 *
 *   import {splitBySpecialCharacters} from '@ion/core/src/utils/';
 *
 *   // default use case
 *   const splitsWOSpl = splitBySpecialCharacters('hello, world!'); // ['hello', 'world']
 *
 *   // include special characters with word as another split
 *   const splitsWithSpl = splitBySpecialCharacters('hello, world!', true); // ['hello', 'hello,', 'world', 'world!']
 *
 * ```
 * @param text The text to split into lists
 * @param includeSpecialCharacter A flag that tells whether to include special characters with word as another split
 * @returns A list of strings that split based on the special characters.
 */
export const splitBySpecialCharacters = (text: string, includeSpecialCharacter = false) => {
  const splits = [];
  let word = '';
  text?.split('').forEach(letter => {
    if (isSpecialCharacter(letter) || letter === ' ') {
      word = word.trim();
      if (word) {
        if (includeSpecialCharacter && letter !== ' ') {
          splits.push(`${word}${letter}`);
        }
        splits.push(word);
      }
      word = '';
    } else {
      word += letter;
    }
  });
  word && splits.push(word);
  return splits;
};

/**
 * The itemsToCSV allows for converting an array of object to an comma-
 * separated string by given key value.
 *
 * ```typescript
 *
 *   import {itemsToCSV} from '@ion/core/src/utils/StringUtil';
 *   const subAccounts = [
 *      {href: 'http://qa.connect.calamp.com:443/connect/services/accounts/36290', rel: "subAccount", status: "Enabled", title: "CA"},
 *      {href: 'http://qa.connect.calamp.com:443/connect/services/accounts/36289', rel: "subAccount", status: "Enabled", title: "Automation"},
 *      {href: 'http://qa.connect.calamp.com:443/connect/services/accounts/36281', rel: "subAccount", status: "Disabled"}
 *   ];
 *   itemsToCSV(subAccounts, 'title'); // "CA, Automation"
 *
 * ```
 */
export function itemsToCSV(arrayObjects: GenericObject[], key: string): string {
  return arrayObjects?.filter(item => key in item).map(item => item[key]).join(', ');
}

/**
 * The itemsToCSV allows for converting an array of object to an comma-
 * separated string by given key value.
 *
 * ```typescript
 *
 *   import {getMatchingCharacters} from '@ion/core/src/utils/';
 *
 *   getMatchingCharacters('Disabling', 'Disabled') // 6
 *   getMatchingCharacters('disablsss', 'Disabled') // 5
 *   getMatchingCharacters('disablsss', 'Disabled', false) // 6
 *   getMatchingCharacters('Ta', 'Tag') // 2
 *
 * ```
 */
export function getMatchingCharacters(
  str: string,
  comparator: string,
  caseSensitive: boolean = true
) {
  return str.split('').filter((el: string) => {
    const compared = caseSensitive ? comparator : comparator.toLowerCase();
    return compared.indexOf(el) !== -1;
  }).length;
}

/**
 * @isValue
 * Returns a boolean value for comparing two strings, defaults to 'none'
 *
 * ```typescript
 *
 *   import {isValue} from '@ion/core/src/utils/StringUtil';
 *
 *   isValue('None', 'none') // true
 *   isValue('None', 'none', true) // false
 *   isValue({name: 'none'}, 'none') // false
 *
 * ```
 */
export function isValue(value: any, match = 'none', caseSensitive = false) {
  if (isString(value)) {
    return !caseSensitive ? value.toLowerCase() === match.toLowerCase() : value === match;
  }
  return false;
};

/**
 * @toLocaleString
 *
 * @param {number | Date} val: number to be formatted to locale
 * @param {string} locale: locale to be passed to toLocaleString
 *
 * ```typescript
 *
 * import {toLocaleString} from '@ion/core/src/utils/';
 *
 * toLocaleString(12345) // '12,345'
 * toLocaleString(12345.678, 'en-US') // '12,345.678'
 * toLocaleString(12345, 'de-DE') // '12.345'
 * toLocaleString(12345.678, 'de-DE') // '12.345,678'
 *
 * ```
 */
export const toLocaleString = (val: any, locale?: string) => {
  if (isNumber(val) || val instanceof Date) {
    return val.toLocaleString(locale || navigator.language);
  }
  return '';
};

/**
 * @stringToNumber
 *
 * @param {string} val: number to be formatted to locale
 *
 * ```typescript
 *
 * import {stringToNumber} from '@ion/core/src/utils/';
 *
 * stringToNumber('12345') // 12345
 * stringToNumber('12,345') // 12345
 * stringToNumber({number: 12345}) // {number: 12345}
 * stringToNumber('text') // NaN
 *
 * ```
 */
export const stringToNumber = (val: string): number | string => {
  if (isString(val)) {
    return parseFloat(val.replace(/,/g, ''));
  }
  return val;
};

/**
 * Converts a title case string to toCamelCase.
 *
 * ```typescript
 *
 * import {toCamelCase} from '@ion/core/src/utils/';
 *
 * toCamelCase('To Camel Case'); // "toCamelCase";
 *
 * ```
 *
 * @param {string} val: string to be camel cased
 */
export const toCamelCase = (val) => {
  return val
    .replace(/\s(.)/g, function(x) { return x.toUpperCase(); })
    .replace(/\s/g, '')
    .replace(/^(.)/, function(x) { return x.toLowerCase(); });
};

/**
 * Converts a camelCase string to Title Case.
 * 
 * ```typescript
 * import {normalizeCamelCase} from '@ion/core/src/utils';
 *
 * normalizeCamelCase('toTitleCase'); // "To Title Case";
 * 
 * ```
 * @param {string} val: string to be Title Cased
 */
export const normalizeCamelCase = (val: string): string => {
  if (typeof val !== 'string' || val.trim() === '') {
    return val;
  }
  return val
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase letters
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle cases like "HTMLTag" or "IPAddress"
    .replace(/^./, (char) => char.toUpperCase()); // Capitalize the first letter
};

/**
 * The replaceLast utility replaces the last occurrence of a substring
 * with a replacement string.
 *
 * ```typescript
 *   import {replaceLast} from '@ion/core/src/utils/';
 *
 *   console.log(replaceLast('/a/b/c', '/', '-'));
 *   // Output: '/a/b-c'
 *
 * ```
 */
export const replaceLast = (value: string, search: string, replacement: string): string => {
  const chars = value.split('');
  const index = value.lastIndexOf(search);
  if (index > -1) {
    const n = index + search.length;
    for (let i = index; i < n; i++) {
      if (i === index) {
        chars[i] = replacement;
      } else {
        delete chars[i];
      }
    }
  }
  return chars.join('');
};

/**
 *
 * Basic utility which provides general formatting of rule requirements,
 * padding commas, and replacing the last comma with 'and' or 'or'.
 *
 * ```typescript
 *   import {formatRequirements} from '@ion/core/src/utils/';
 *
 *   console.log(formatRequirements(['Override Bus Value - Speed must be either Yes,No.'], 'or'));
 *   // Output: 'Override Bus Value - Speed must be either Yes or No.'
 *
 * ```
 */
export const formatRequirements = (rules: string[], replacement: string) => {
  const values = rules.toString().replace(/,/g, ', ');
  return replaceLast(values, ',', ` ${replacement}`).replace(/\s\s+/g, ' ');
};

/**
 * Converts a camelCaseString to a title based string, for example,
 * given the string "someCamelCaseString", will convert the string
 * to "Some Camel Case String" and return the result.
 *
 * ```typescript
 *
 *    console.log(toTitle('name')); // "Name";
 *    console.log(toTitle('thisIsATest')); // "This Is A Test";
 *
 * ```
 *
 * @method toTitle
 */
export const toTitle = (val: string) => {
  const pascalCase = val.charAt(0).toUpperCase() + val.substr(1);
  return pascalCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z])([0-9])/gi, '$1 $2')
    .replace(/([0-9])([a-z])/gi, '$1 $2');
};

/**
 * @method hexToRgb
 *
 * Provides a utility to convert HEX colors to RGB Object
 *
 * ```typescript
 *
 *   import {hexToRgb} from '@ion/dls/src/util';
 *
 *   const rgb = hexToRgb('#0033ff'); // {r: 0, g: 51, b: 255}
 *
 * ```
 *
 */
export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
};

/**
 * The regExpEscape utility handles to escape RegExp special characters
 *
 * ```typescript
 *
 *   import {regExpEscape} from '@ion/dls/src/util';
 *
 *   search = regExpEscape('(No'); // '\\(No'
 *
 * ```
 */
export const regExpEscape = v => v?.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

/**
 * The padStart add the prefix to the string and padd it based on
 * the length of the string and amount
 *
 * ```typescript
 *
 *   import {padStart} from '@ion/dls/src/util';
 *
 *   const str1 = padStart(5, 0); // 05
 *   const str2 = padStart(11, 0); // 11
 *
 * ```
 */

/**
 * The padStart add the prefix to the string and padd it based on
 * the length of the string and amount
 *
 * ```typescript
 *
 *   import {padStart} from '@ion/dls/src/util';
 *
 *   const str1 = padStart(5, 0); // 05
 *   const str2 = padStart(11, 0); // 11
 *
 * ```
 */
export const padStart = (str: PadParam, prefix: PadParam, amount = 2) => (
  `${prefix}${str}`.slice(-amount)
);

type ParseValueWithKey = {
  source: string;
  key: string;
  endingDelimiter?: string;
  customValueLength?: number;
}

/**
 * Parses and returns a value from a given source string from a given key.
 *
 * ```typescript
 *  import {parseValueWithKey} from '@ion/core/src/utils/';
 *
 *  // value results in 12345
 *  const value = parseValueWithKey({
 *    source: 'VIN-INFO: VIN=12345,12344DDD',
 *    key: 'VIN=',
 *    endingDelimiter: ','
 *  });
 *
 *  // value results in 0123456789 since we aren't sending a delimiter, defaults to 10 characters length
 *  const value = parseValueWithKey({
 *    source: 'VIN-INFO: VIN=0123456789,12344DDD',
 *    key: 'VIN='
 *  });
 *
 *  // value results in 01234 when given no delimiter and an updated default value length
 *  const value = parseValueWithKey({
 *    source: 'VIN-INFO: VIN=01234,12344DDD',
 *    key: 'VIN='
 *    customValueLength: 5
 *  });
 *
 * ```
 *
 * @param source the string to search the value & key for
 * @param key the key before the value
 * @param endingDelimiter string delimiter denoting the end of the value, do not supply if using customValueLength
 * @param customValueLength a number that can be provided instead of an ending delimiter if the value length is known
 * @returns the value starting at the end of the given key found in the source
 *          the source if the key is not located within it
 *          otherwise empty string
 */
export const parseValueWithKey = ({
  source,
  key,
  endingDelimiter,
  customValueLength = 10
}: ParseValueWithKey): string => {
  if (source) {
    try {
      const keyStart = source.indexOf(key);
      if (keyStart > -1) {
        const valueStart = keyStart + key.length;
        const valueEnd = source.indexOf(endingDelimiter, valueStart);
        const resultingValueEnd = valueEnd > -1
          ? valueEnd
          : valueStart + customValueLength;
        return source.slice(valueStart, resultingValueEnd);
      }
      return source;
    } catch (error) {
      error(`There is an issue parsing the value of the key ${key} in the source ${source}. Please check your data.`);
    }
  }
  return '';
};

/**
 * Provides a safe-guard utility which ensures create regex expression
 * within a try / catch block, returning the expression result if valid, otherwise
 * returns same source.
 *
 * ```typescript
 *
 *  import {createRegExp} from '@ion/core/src/utils/';
 *
 *  createRegExp('mkumar)'); // output:  /mkumar\)/gi
 *  createRegExp('(US'); // output: /\(/US/gi
 *  createRegExp('(Admin))'); // output: /\(/Admin\)/gi
 * ```
 */
export const createRegExp = (source: string, flag = 'ig'): RegExp => {
  try {
    return new RegExp(regExpEscape(source), flag);
  } catch (err) {
    error(`There is an issue creating regex experssion to the value ${source} and the reason is ${err.message}`);
  }
  return source as unknown as RegExp;
};

/**
 * Provides a utility to remove all extra white spaces
 *
 * ```typescript
 *
 *   import {removeStringExtraSpace} from '@ion/core/src/utils/';
 *
 *   removeStringExtraSpace(' Last Week ') // 'Last Week'
 *   or
 *   removeStringExtraSpace(' ') // ''
 *
 * ```
 */
export const removeStringExtraSpace = (val: string) => {
  return val.replace(/\s/g, ' ').trim();
};

/**
 * Provides a utility which removes additional whitespace from the given string;
 * i.e. the resulting string will only contain single white-space characters
 * (newlines will be preserved).
 *
 * Similar to the `normalize` function, this utility is primarily provided as a
 * convenience to be used by clients in order to aid in readability of multi-line
 * string constants while ensuring the additional white space is removed when used
 * for display.
 *
 * ```typescript
 * const results = `Completed Measuring: ${items.length}
 *    * Total Time: ${totalTime}
 *    * Total Service Time: ${totalServiceTime}
 *    * Total UI Time: ${totalUITime}
 * `;
 * console.log(results); // results with additional whitespace removed ...
 * ```
 *
 * @see     {@link normalize}
 * @param   val The string for which additional whitespace is to be removed.
 * @returns The given string with all additional whitespace removed, i.e. t
 *          the string will only contain single white-space characters, and
 *          newlines will be preserved.
 */
export const removeAdditionalWhiteSpace = (val: string): string => (
  val.replace(/[ \t]{2,}/g, ' ')
);

/**
 * Provides a utility which determines if the given string contains consecutive
 * whitespace characters.
 *
 * ```typescript
 * const source = 'string with  consecutive  white   space characters';
 * console.log(containsConsecutiveWhitespace(source)); // true
 *
 * ...
 *
 * const source = 'string with no consecutive whitespace characters';
 * console.log(containsConsecutiveWhitespace(source)); // false
 * ```
 *
 * @param   val The string which is to be tested for consecutive whitespace
 *          characters.
 * @returns `true` if the string contains consecutive whitespace characters,
 *          otherwise, `false`.
 */
export const containsConsecutiveWhitespace = (val: string): boolean => (
  /[ \t]{2,}/.test(val)
);

/**
 * The isFirstCharSpace utility returns true if start or first character
 * of a string is empty or a whitespace
 *
 * @method isFirstCharSpace
 */
export const isFirstCharSpace = (val: string) => /^\s/.test(val);

/**
 * The keyFileBuilderInputPattern utility returns true if matchecs the pattern /^[A-Fa-f0-9_]*$/
 *
 */
export const keyFileBuilderInputPattern = (val: string) => /^[A-Fa-f0-9_]*$/.test(val);

/**
 * The isHexString utility returns true if val is a valid hex string
 * of a certain length if specified
 *
 * @method isHexString
 */
export const isHexString = (val: string, len?: number) => {
  const regexp = new RegExp(`^[a-f0-9]${len?`{${len}}`:'*'}$`, 'gi');
  return val?.match(regexp)?.length > 0;
};

/**
 * Provides a utility to convert a null or undefined value
 * to an empty string.
 *
 * ```typescript
 *
 * import {undefinedAndNullToString} from '@ion/core/src/utils/';
 *
 * undefinedAndNullToString(null); // '';
 * undefinedAndNullToString(undefined); // '';
 * undefinedAndNullToString('Apple'); // 'Apple';
 *
 * ```
 *
 * @param {null | undefined | string} val: undefined or null or string
 */
export function undefinedAndNullToString(val: null | undefined | string) {
  return isNull(val) || isUndefined(val) ? '' : val;
};

/**
 * Provides an utility to check the string has special characters except Asterisk
 * or not.
 * * ```typescript
 *
 * import {hasSpecialChars} from '@ion/core/src/utils/';
 *
 * hasSpecialChars(++Test); // true;
 * hasSpecialChars(test device); // false;
 * hasSpecialChars('^Test Devices!'); // true;
 * hasSpecialChars('*Test'); // false;
 * hasSpecialChars('Test*'); // false;
 */
export const hasSpecialChars = (searchTerm: string | any): boolean => {
  const specialCharectors = /[!@#$%^&_+\-=\[\]{};':"\\|,.<>\/?]/;
  return specialCharectors.test(searchTerm);
};

/**
 * Provides a convenience function which removes duplicate consecutive characters
 * from the given string and replaces them with a single character. This utility
 * is generally used to removed double slashes from URIs, but can also be utilized
 * for any use-case where consecutive duplicate character instances should be
 * replaced with just a single occurrence of the character.
 *
 * ```typescript
 *  import {dedupeChars} from '@ion/core/src/utils/StringUtil';
 *  ...
 *
 *  const url = dedupeChars(href);
 * ```
 * @param   value The string to against which consecutive duplicate characters are
 *          to be removed.
 * @param   char The characters in question which is to be normalized (replaced
 *          with a single instance). Defaults to '/'.
 * @returns The string with consecutive occurrences of the specified character
 *          replaced by a single occurrence.
 */
export const dedupeChars = (value: string, char: string = '/'): string => {
  const escaped = char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return value.replace(new RegExp(`${escaped}{2,}`, 'g'), char);
};

/**
 * Splits a string by a specified delimiter and processes the terms based on the
 * provided options.
 * 
 * ```typescript
 *   import {processDelimiters} from '@ion/core/src/utils/StringUtil';
 *   ...
 * 
 *   processDelimiters('apple, orange, banana', {
 *     splitOnly: true
 *   });
 *   // ['apple', 'orange', 'banana']
 * 
 *   processDelimiters('apple orange banana', {
 *     isExact: true 
 *   });
 *   // 'apple OR orange OR banana'
 * 
 *   processDelimiters('apple, orange, banana', {
 *     isExact: false,
 *     joinWith: 'AND'
 *   });
 *   // '*apple* AND *orange* AND *banana*'
 * ```
 *
 * @param   source The source string for which to split and process based on the 
 *          provided options.
 * @param   options An object specifying the behavior of the function.
 * @param   options.isExact Whether to join terms without wrapping. Defaults to 
 *          `false`.
 * @param   options.splitOnly Whether to return the split terms as-is. Defaults to 
 *          `false`.
 * @param   options.joinWith The delimiter for which to join the terms. Defaults to 
 *          `false`.
 * @param   options.delimiters The Delimiters which are to be used when processing
 *          the given `source`. Defaults to `[',', ' '] `.
 * @returns The processed string or array of split terms based on the options.
 */
export const processDelimiters = (
  source: string | undefined,
  options: {
    isExact?: boolean;
    splitOnly?: boolean;
    joinWith?: string;
    wrapEach?: boolean;
    delimiters?: string[];
    trim?: boolean;
  } = {}
): string[] | string | undefined => {
  if (source) {
    const {
      isExact = false,
      splitOnly = false,
      wrapEach = false,
      joinWith = 'OR',
      delimiters = [',', ' '],
      trim = false
    } = options;
    const value = trim ? removeAdditionalWhiteSpace(source) : source;
    const delimiter = delimiters.find((item: string) => value.includes(item));
    if (delimiter) {
      const terms = value.split(delimiter).map(term => term.trim()).filter(term => term.length > 0);
      if (!splitOnly) {
        let processedTerms = terms;
        if (!isExact) {
          processedTerms = terms.map(term => `*${term}*`);
        } else if (wrapEach) {
          processedTerms = terms.map(term => `(${term})`);
        }
        return processedTerms.join(` ${joinWith} `);
      }
      return terms;
    }
  }
  return [source];
};

/**
 * Determines if the provided `string` contains occurrences of the specified delimiter(s).
 * 
 * ```typescript
 *   containsDelimiters(
 *     'testing, something', ','
 *   );  // true
 * 
 *   containsDelimiters(
 *     'testing, something.else', ',', '.'
 *   );  // true
 * 
 *   containsDelimiters(
 *     'testing something else for commas', ','
 *   );  // false
 * ```
 *
 * @see     {@link contains}
 * @param   source The source string for which occurrences of the given delimiters.
 * @param   delimiters A delimiter string, or multiple delimiter strings against
 *          which to determine if the given source contains occurrences.
 * @returns `true` if the string contains any of the delimiters, otherwise, `false`.
 */
export const containsDelimiters = (source: string, ...delimiters: string[]) => (
  delimiters.some(delimiter => contains(source, delimiter))
);

/**
 * The splitByCommaCharacters utility function is used to split a string into comma characters.
 *
 *
 * ```typescript
 *
 *   import {splitByCommaCharacters} from '@ion/core/src/utils/';
 *
 *   // default use case
 *   const splitsWithSpl = splitByCommaCharacters('hello, world'); // ['*hello, *world']
 *
 * ```
 * @param text The text to split into lists
 * @returns A Strings that split based on the comma, partial characters.
 */
export const splitByCommaCharacters = (text: string) => {
  const terms = text?.split(/\s+/)?.filter(term => term?.trim() !== '');
  return terms?.map(term => `*${term}*`)?.join(', ');
};

/**
 * The normalizeClauseList function provides a simple utility which normalizes a single field clause
 * that contains a multi-value list joined by an operator, by wrapping the value list in parentheses.
 *
 * This is useful when users type queries like:
 * `jobId:1 OR 2 OR 3`, which should be normalized to: `jobId:(1 OR 2 OR 3)`.
 *
 * ```typescript
 *
 *   import {normalizeClauseList} from '@ion/core/src/utils/';
 *
 *   const normalized = normalizeClauseList(
 *     '(username:(user01)) AND (jobId:354797 OR 353096 OR 353095)',
 *     {field: 'jobId', operator: 'OR'}
 *   );
 *   // output: '(username:(user01)) AND (jobId:(354797 OR 353096 OR 353095))'
 *
 * ```
 */
export const normalizeClauseList = (
  query: string,
  options: NormalizeClauseOptions
): string => {
  let result = query;

  if (result) {
    const {
      field,
      operator,
      caseSensitive = false
    } = options;

    const fieldRe = escapeRegExp(field);

    if (!new RegExp(`\\b${fieldRe}\\s*:\\s*\\(`, caseSensitive ? '' : 'i').test(result)) {
      const parenthesized = result.replace(
        new RegExp(
          `\\(\\s*${fieldRe}\\s*:\\s*([^()]*?\\b${operator}\\b[^()]*)\\)`,
          caseSensitive ? '' : 'i'
        ),
        (_m: string, inner: string) => {
          return `(${field}:(${inner.trim()}))`;
        }
      );

      if (parenthesized !== result) {
        result = parenthesized;
      } else {
        result = result.replace(
          new RegExp(
            `\\b${fieldRe}\\s*:\\s*([^\\s()]+(?:\\s*\\b${operator}\\b\\s*[^\\s()]+)+)`,
            caseSensitive ? 'g' : 'gi'
          ),
          (_m: string, list: string) => {
            return `${field}:(${list.trim()})`;
          }
        );
      }
    }
  }

  return result;
};
